<template>
  <section class="why-attend-section section-padding bg-blue-light">
    <div class="container">
      <!-- Section Header -->
      <div class="section-header text-center mb-5">
        <h2 class="section-title text-gradient-primary">
          Why Attend IDWS 2026?
        </h2>
        <p class="section-subtitle">
          Discover the transformative benefits of joining Africa's premier digital workplace summit
        </p>
      </div>

      <!-- Features Grid -->
      <div class="features-grid">
        <div class="row g-4">
          <div
            v-for="(feature, index) in whyAttendFeatures"
            :key="feature.id"
            class="col-lg-3 col-md-6"
          >
            <FeatureCard
              :title="feature.title"
              :description="feature.description"
              :icon="feature.icon"
              :icon-color="feature.iconColor"
              :variant="feature.variant"
              :hoverable="true"
              :show-footer="false"
              :show-decoration="true"
              :custom-class="`animate-fade-in feature-${index}`"
              @click="handleFeatureClick(feature)"
            />
          </div>
        </div>
      </div>

      <!-- Enhanced Stats Section -->
      <div class="stats-section mt-5">
        <div class="row justify-content-center">
          <div class="col-lg-10">
            <div class="stats-container card-glass p-4">
              <div class="row g-4 text-center">
                <div v-for="stat in summitStats" :key="stat.label" class="col-6 col-md-3">
                  <div class="stat-item hover-lift">
                    <div class="stat-icon-container mb-3">
                      <SvgIcon :name="stat.icon" size="2xl" :color="stat.color" />
                    </div>
                    <div class="stat-value" :class="`text-${stat.color}`">
                      {{ stat.value }}
                    </div>
                    <div class="stat-label">{{ stat.label }}</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Key Benefits Grid -->
      <div class="benefits-section mt-5">
        <div class="row justify-content-center">
          <div class="col-lg-8 text-center mb-4">
            <h3 class="benefits-title">What Makes IDWS Different</h3>
            <p class="benefits-subtitle">
              Experience unparalleled learning, networking, and growth opportunities
            </p>
          </div>
        </div>

        <div class="row g-4">
          <div
            v-for="(benefit, index) in keyBenefits"
            :key="benefit.id"
            class="col-lg-6"
          >
            <div class="benefit-card hover-lift">
              <div class="benefit-content">
                <div class="benefit-header">
                  <div class="benefit-icon-wrapper" :class="`bg-${benefit.color}`">
                    <SvgIcon :name="benefit.icon" size="xl" color="white" />
                  </div>
                  <div class="benefit-title-section">
                    <h4 class="benefit-title">{{ benefit.title }}</h4>
                    <span class="benefit-badge badge" :class="`bg-${benefit.color}`">
                      {{ benefit.badge }}
                    </span>
                  </div>
                </div>
                <p class="benefit-description">{{ benefit.description }}</p>
                <ul class="benefit-highlights">
                  <li v-for="highlight in benefit.highlights" :key="highlight" class="benefit-highlight">
                    <SvgIcon name="check-circle" size="sm" color="success" class="me-2" />
                    {{ highlight }}
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>

<script>
import { onMounted } from 'vue'
import { animate, stagger } from 'animejs'
import FeatureCard from './FeatureCard.vue'
import SvgIcon from './SvgIcon.vue'

export default {
  name: 'WhyAttendSection',
  components: {
    FeatureCard,
    SvgIcon
  },
  setup() {
    const whyAttendFeatures = [
      {
        id: 1,
        title: 'Global Network',
        description: 'Connect with 300+ digital workplace leaders, HR professionals, and technology pioneers from across Africa and beyond.',
        icon: 'users',
        iconColor: 'primary',
        variant: 'primary'
      },
      {
        id: 2,
        title: 'Innovation Hub',
        description: 'Discover cutting-edge workplace technologies, AI solutions, and digital transformation strategies that drive results.',
        icon: 'lightbulb',
        iconColor: 'success',
        variant: 'success'
      },
      {
        id: 3,
        title: 'Expert Knowledge',
        description: 'Learn from world-renowned thought leaders, industry experts, and successful transformation case studies.',
        icon: 'award',
        iconColor: 'warning',
        variant: 'warning'
      },
      {
        id: 4,
        title: 'Business Growth',
        description: 'Transform your organization with actionable insights, proven strategies, and innovative solutions for sustainable growth.',
        icon: 'graph-up',
        iconColor: 'primary',
        variant: 'primary'
      }
    ]

    const summitStats = [
      {
        value: '300+',
        label: 'Global Delegates',
        icon: 'users',
        color: 'primary'
      },
      {
        value: '15',
        label: 'Speakers',
        icon: 'people',
        color: 'success'
      },
      {
        value: '15+',
        label: 'Countries',
        icon: 'globe',
        color: 'warning'
      },
      {
        value: '3',
        label: 'Days',
        icon: 'calendar',
        color: 'primary'
      }
    ]

    const keyBenefits = [
      {
        id: 1,
        title: 'Premium Learning Experience',
        description: 'Access exclusive workshops, masterclasses, and hands-on sessions designed by industry leaders to accelerate your professional growth.',
        icon: 'graduation-cap',
        color: 'primary',
        badge: 'Expert-Led',
        highlights: [
          'Interactive workshops with real case studies',
          'Certification opportunities',
          'Access to exclusive research and reports',
          'Post-summit learning resources'
        ]
      },
      {
        id: 2,
        title: 'Strategic Networking',
        description: 'Build lasting professional relationships through structured networking sessions, executive roundtables, and exclusive VIP events.',
        icon: 'handshake',
        color: 'success',
        badge: 'High-Value',
        highlights: [
          'C-level executive meetups',
          'Industry-specific networking sessions',
          'Partnership opportunity marketplace',
          'Alumni network access'
        ]
      },
      {
        id: 3,
        title: 'Innovation Showcase',
        description: 'Experience the latest workplace technologies through live demonstrations, product showcases, and innovation labs.',
        icon: 'rocket',
        color: 'warning',
        badge: 'Cutting-Edge',
        highlights: [
          'Live technology demonstrations',
          'Innovation lab access',
          'Startup pitch competitions',
          'Future of work exhibits'
        ]
      },
      {
        id: 4,
        title: 'Actionable Insights',
        description: 'Take home practical strategies, implementation frameworks, and proven methodologies to transform your workplace immediately.',
        icon: 'target',
        color: 'primary',
        badge: 'Results-Driven',
        highlights: [
          'Ready-to-implement frameworks',
          'Digital transformation playbooks',
          'ROI measurement tools',
          '90-day action plans'
        ]
      }
    ]

    const handleFeatureClick = (feature) => {
      console.log('Feature clicked:', feature.title)
      // Could implement modal with more details or scroll to relevant section
    }

    onMounted(() => {
      // Animate features with stagger
      animate('.feature-card', {
        opacity: [0, 1],
        translateY: [30, 0],
        scale: [0.95, 1],
        duration: 600,
        delay: stagger(100),
        easing: 'easeOutExpo'
      })

      // Animate stats with different timing
      animate('.stat-item', {
        opacity: [0, 1],
        translateY: [20, 0],
        duration: 500,
        delay: stagger(80, {start: 300}),
        easing: 'easeOutExpo'
      })

      // Animate benefits
      animate('.benefit-card', {
        opacity: [0, 1],
        translateX: [-30, 0],
        duration: 700,
        delay: stagger(150, {start: 600}),
        easing: 'easeOutExpo'
      })

      // Set up intersection observer for scroll animations
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            animate(entry.target.querySelectorAll('.animate-fade-in'), {
              opacity: [0, 1],
              translateY: [30, 0],
              duration: 600,
              delay: stagger(100),
              easing: 'easeOutExpo'
            })
          }
        })
      })

      document.querySelectorAll('.why-attend-section').forEach(section => {
        observer.observe(section)
      })
    })

    return {
      whyAttendFeatures,
      summitStats,
      keyBenefits,
      handleFeatureClick
    }
  }
}
</script>

<style scoped>
.why-attend-section {
  position: relative;
  overflow: hidden;
}

.why-attend-section::before {
  content: '';
  position: absolute;
  top: -50%;
  left: -50%;
  width: 200%;
  height: 200%;
  background: linear-gradient(45deg, transparent 30%, rgba(37, 99, 235, 0.03) 50%, transparent 70%);
  animation: backgroundShift 20s ease-in-out infinite;
}

@keyframes backgroundShift {
  0%, 100% { transform: translateX(-10%) translateY(-10%); }
  50% { transform: translateX(10%) translateY(10%); }
}

.section-header {
  position: relative;
  z-index: 2;
}

.section-title {
  font-size: var(--font-size-4xl);
  font-weight: 700;
  margin-bottom: var(--spacing-lg);
  font-family: var(--font-primary);
}

.section-subtitle {
  font-size: var(--font-size-xl);
  color: var(--idws-text-muted);
  max-width: 600px;
  margin: 0 auto;
  line-height: 1.5;
}

.features-grid {
  position: relative;
  z-index: 2;
}

.features-grid .row {
  align-items: stretch;
}

.features-grid .col-lg-3,
.features-grid .col-md-6 {
  display: flex;
}

/* Stats Section */
.stats-section {
  position: relative;
  z-index: 2;
}

.stats-container {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(20px);
  border-radius: 24px;
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: var(--shadow-xl);
}

.stat-item {
  transition: all 0.3s ease;
  cursor: pointer;
}

.stat-item:hover {
  transform: translateY(-4px);
}

.stat-icon-container {
  background: var(--idws-blue-bg-light);
  width: 80px;
  height: 80px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto;
  border: 2px solid var(--idws-blue-bg-soft);
  transition: all 0.3s ease;
}

.stat-item:hover .stat-icon-container {
  transform: scale(1.1);
  box-shadow: var(--shadow-md);
}

.stat-value {
  font-size: var(--font-size-3xl);
  font-weight: 700;
  margin: var(--spacing-md) 0 var(--spacing-sm);
  font-family: var(--font-primary);
}

.stat-label {
  font-size: var(--font-size-base);
  color: var(--idws-text-muted);
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

/* Benefits Section */
.benefits-section {
  position: relative;
  z-index: 2;
}

.benefits-title {
  font-size: var(--font-size-2xl);
  font-weight: 700;
  color: var(--idws-text-dark);
  margin-bottom: var(--spacing-md);
  font-family: var(--font-primary);
}

.benefits-subtitle {
  font-size: var(--font-size-lg);
  color: var(--idws-text-muted);
  line-height: 1.5;
}

.benefit-card {
  background: white;
  border-radius: 20px;
  padding: var(--spacing-2xl);
  border: 1px solid var(--idws-border-gray);
  transition: all 0.4s ease;
  height: 100%;
  box-shadow: var(--shadow-sm);
}

.benefit-card:hover {
  transform: translateY(-6px);
  box-shadow: var(--shadow-lg);
  border-color: var(--idws-bright-blue);
}

.benefit-header {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-lg);
  margin-bottom: var(--spacing-lg);
}

.benefit-icon-wrapper {
  width: 70px;
  height: 70px;
  border-radius: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  transition: all 0.3s ease;
}

.benefit-card:hover .benefit-icon-wrapper {
  transform: scale(1.1);
}

.benefit-title-section {
  flex-grow: 1;
}

.benefit-title {
  font-size: var(--font-size-xl);
  font-weight: 700;
  color: var(--idws-text-dark);
  margin-bottom: var(--spacing-sm);
  font-family: var(--font-primary);
}

.benefit-badge {
  font-size: var(--font-size-sm);
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  font-weight: 600;
}

.benefit-description {
  font-size: var(--font-size-base);
  color: var(--idws-text-muted);
  line-height: 1.6;
  margin-bottom: var(--spacing-lg);
}

.benefit-highlights {
  list-style: none;
  padding: 0;
  margin: 0;
}

.benefit-highlight {
  display: flex;
  align-items: center;
  margin-bottom: var(--spacing-sm);
  font-size: var(--font-size-base);
  color: var(--idws-text-dark);
}

.benefit-highlight:last-child {
  margin-bottom: 0;
}

/* Color Variants */
.bg-primary {
  background: var(--idws-gradient-primary) !important;
}

.bg-success {
  background: var(--idws-gradient-secondary) !important;
}

.bg-warning {
  background: var(--idws-gradient-accent) !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .section-title {
    font-size: var(--font-size-2xl);
  }

  .section-subtitle {
    font-size: var(--font-size-base);
  }

  .stats-container {
    margin: 0 var(--spacing-md);
  }

  .benefit-card {
    padding: var(--spacing-xl);
    margin-bottom: var(--spacing-lg);
  }

  .benefit-header {
    flex-direction: column;
    text-align: center;
    gap: var(--spacing-md);
  }

  .benefit-icon-wrapper {
    width: 60px;
    height: 60px;
    margin: 0 auto;
  }

  .stat-icon-container {
    width: 60px;
    height: 60px;
  }

  .stat-value {
    font-size: var(--font-size-2xl);
  }
}

@media (max-width: 576px) {
  .benefit-highlights {
    text-align: left;
  }

  .stat-value {
    font-size: var(--font-size-xl);
  }

  .stat-label {
    font-size: var(--font-size-sm);
  }
}
</style>